import { Box, Button, Card, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DraggableQuestionCard from './DraggableQuestionCard';
import { SurveyV2 } from '@act/shared/data-transfer-objects';

interface QuestionListProps {
  content: SurveyV2.CreateSurveyJSONDto;
  onQuestionsChange: (questions: SurveyV2.CreateQuestionJSONDto[]) => void;
  onDeleteQuestion?: (questionIndex: number) => void;
  formErrors?: any; // Form validation errors from React Hook Form
}

const QuestionList = ({
  content,
  onQuestionsChange,
  onDeleteQuestion,
  formErrors,
}: QuestionListProps) => {
  const questions = content?.surveyContent?.surveyVersion?.questions || [];

  const addQuestion = () => {
    const newQuestion = {
      ...SurveyV2.DEFAULT_QUESTION,
      sortOrder: questions.length + 1,
      key: `question-${questions.length + 1}`,
    };
    onQuestionsChange([...questions, newQuestion]);
  };

  const handleUpdateQuestion = (
    updatedQuestion: SurveyV2.CreateQuestionJSONDto
  ) => {
    const updatedQuestions = questions.map(quest =>
      quest.key === updatedQuestion.key ? updatedQuestion : quest
    );
    onQuestionsChange(updatedQuestions);
  };

  const handleMoveQuestion = (dragIndex: number, hoverIndex: number) => {
    const dragQuestion = questions[dragIndex];
    const newQuestions = [...questions];
    newQuestions.splice(dragIndex, 1);
    newQuestions.splice(hoverIndex, 0, dragQuestion);

    const updatedQuestions = newQuestions.map((quest, index) => ({
      ...quest,
      sortOrder: index + 1,
      key: `quest-${index + 1}`,
    }));

    onQuestionsChange(updatedQuestions);
  };

  function handleRemoveQuestion(sortOrder: number): void {
    const questionIndex = questions.findIndex(q => q.sortOrder === sortOrder);

    if (onDeleteQuestion) {
      onDeleteQuestion(questionIndex);
    } else {
      const updatedQuestions = questions
        .filter(quest => quest.sortOrder !== sortOrder)
        .map((quest, index) => ({
          ...quest,
          key: `quest-${index + 1}`,
          sortOrder: index + 1,
        }));
      onQuestionsChange(updatedQuestions);
    }
  }

  if (questions?.length === 0) {
    return (
      <Box sx={{ width: '100%', margin: '0 auto' }}>
        <Card sx={{ padding: '32px', textAlign: 'center' }}>
          <Typography variant="h6" sx={{ marginBottom: '24px' }}>
            Add a question to get started!
          </Typography>
          <Button
            startIcon={<AddIcon />}
            onClick={addQuestion}
            variant="contained"
          >
            Add Question
          </Button>
        </Card>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: '100%',
        margin: '0 auto',
        display: 'flex',
        flexDirection: 'column',
        gap: '24px',
      }}
    >
      {questions?.map((question, index) => (
        <DraggableQuestionCard
          key={question.key}
          question={question}
          index={index}
          questionCount={questions.length}
          isActionSurvey={content?.surveyContent?.isActionSurvey || false}
          handleMoveQuestion={handleMoveQuestion}
          handleUpdateQuestion={handleUpdateQuestion}
          handleRemoveQuestion={handleRemoveQuestion}
          formErrors={formErrors}
        />
      ))}
      <Button
        startIcon={<AddIcon />}
        onClick={addQuestion}
        variant="contained"
        sx={{ alignSelf: 'center' }}
      >
        Add Question
      </Button>
    </Box>
  );
};

export default QuestionList;
